import { preserveCanvasScroll } from "@/lib/cms-puck/preserve-canvas-scroll";
import { useSyncExternalStore, type ReactNode } from "react";

/**
 * Store EXTERNE générique pour les drawers des champs CMS avancé.
 *
 * Pourquoi ? Ouvrir un drawer depuis le rendu d'un champ Puck le rend fragile :
 * monter un contenu lourd (formulaire, upload…) DÉSÉLECTIONNE le bloc → Puck
 * démonte le panneau de champs → le `StyledDrawer` et son state local
 * disparaissent (drawer « impossible à ouvrir »). On déporte donc le drawer dans
 * un HOST unique monté HORS du panneau (`PuckDrawerHost`, branché via
 * `overrides.puck` dans editor.tsx). Le champ ne fait qu'OUVRIR le drawer via ce
 * store en fournissant le CONTENU (self-contained : il porte son propre state et
 * capture l'`onChange` du champ au moment du clic). Même pattern que le picker
 * média (`media-picker-store`), mais générique (node arbitraire).
 */

type PuckDrawerState = {
  open: boolean;
  title: ReactNode;
  maxWidth?: string;
  node: ReactNode | null;
};

let state: PuckDrawerState = {
  open: false,
  title: "",
  maxWidth: undefined,
  node: null,
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Ouvre le drawer hôte avec un contenu (node) + titre + largeur. */
export function openPuckDrawer(opts: {
  title: ReactNode;
  maxWidth?: string;
  node: ReactNode;
}): void {
  preserveCanvasScroll();
  state = {
    open: true,
    title: opts.title,
    maxWidth: opts.maxWidth,
    node: opts.node,
  };
  emit();
}

/** Ferme le drawer hôte (le contenu reste monté le temps de l'animation). */
export function closePuckDrawer(): void {
  state = { ...state, open: false };
  emit();
}

export function usePuckDrawer(): PuckDrawerState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}
